import React, { useState, useEffect, useRef } from "react";
import useApiToken from "../../hooks/useApiToken";
import {
  AssessmentQuestionnaire,
  AssessmentTemplate,
  Candidate,
  RoleStatus,
} from "../../Models";
import useClickOutside from "../../hooks/useClickOutside";
import {
  ArrowRightCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  TrophyIcon,
  UserCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import { AcademicCapIcon, CheckBadgeIcon } from "@heroicons/react/20/solid";
import { Spinner } from "../Shared/Spinner";
import { createPortal } from "react-dom";
import { GetCandidate, GetCandidateScore } from "../../api/candidates.api";
import { GetAssessmentForRole } from "../../api/assessmentquestionnaires.api";
import { GetQuestionAnsweredByCandidate } from "../../api/candidates.api";

interface Question {
  id: number;
  question: string;
  result: "correct" | "wrong" | "skipped";
  accuracy: number;
  skills: { id: string; name: string }[];
  time: number;
  type?: string;
  candidateAnswerTexts?: string[];
  correctAnswerTexts?: string[];
  manualEvaluation?: number | null;
}

interface Skill {
  name: string;
  score: number;
  questionsTotal: number;
  questionsAnswered: number;
  questionsCorrect: number;
  questionsWrong: number;
  questionsSkipped: number;
  questions: Question[];
}

interface Test {
  id: number;
  skills: Skill[];
}

interface CandidateAssessmentResultProps {
  candidateId: string;
  roleId: string;
  onClose: () => void;
  initialRoleStatusId?: string;
}

interface CandidateScore {
  score: number;
  roleStatus: RoleStatus;
  roleAssessmentMappingId: string;
  countOfTimesMouseOutOfTestWindow: number;
  startTime: Date;
  endTime: Date | null;
}

interface RoleAssessment {
  id: string;
  assessment: AssessmentTemplate;
  questionairre: AssessmentQuestionnaire;
  skills: { id: string; name: string };
}

const Alert: React.FC<{
  variant?: "default" | "destructive";
  children: React.ReactNode;
}> = ({ variant = "default", children }) => {
  const variantStyles = {
    default: "bg-blue-50 text-blue-700 border-blue-200",
    destructive: "bg-red-50 text-red-700 border-red-200",
  };

  return (
    <div className={`p-4 rounded-lg border ${variantStyles[variant]}`}>
      {children}
    </div>
  );
};

const AlertDescription: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return <div className="text-sm">{children}</div>;
};

const CandidateAssessmentResult = ({
  candidateId,
  roleId,
  onClose,
  initialRoleStatusId,
}: CandidateAssessmentResultProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>("sdasdfs");
  const [assessmentData, setAssessmentData] = useState<Test | null>(null);
  const [candidate, setCandidate] = useState<Candidate>();
  const [candidateScore, setCandidateScore] = useState<CandidateScore>();
  const [assessments, setAssessments] = useState<RoleAssessment[]>([]);
  const [currentAssessment, setCurrentAssessment] = useState<RoleAssessment>();
  const [selectedSkill, setSelectedSkill] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [expandedQuestionIds, setExpandedQuestionIds] = useState<Set<number>>(new Set());
  // const [allQuestions, seAllQuestions] = useState<Question[]>([]);
  const modalRef = useRef(null);
  const token = useApiToken();

  useClickOutside(modalRef, () => {
    onClose();
  });

  useEffect(() => {
    if (!token) return;
    GetCandidate(candidateId, token).then((cd) => {
      setCandidate(cd);
    });
    GetAssessmentForRole(roleId, candidateId, token).then(
      (a: RoleAssessment[]) => {
        const filtered = a.filter((a) => a.questionairre != null);
        setAssessments(filtered);
        if (filtered.length > 0) {
          // Pre-select the assessment whose roleStatus matches the clicked column;
          // fall back to the first assessment if no match is found.
          const match = initialRoleStatusId
            ? filtered.find((x) => x.id === initialRoleStatusId)
            : undefined;
          setCurrentAssessment(match ?? filtered[0]);
        }
      }
    );
  }, [roleId, token, candidateId]);

  // useEffect(() => {
  //   if (!currentAssessment?.questionairre.id) return;
  //   getAllQuestionsByQuestionnareId(currentAssessment?.questionairre.id).then(
  //     (q) => {
  //       seAllQuestions(q);
  //     }
  //   );
  // }, [currentAssessment]);
  useEffect(() => {
    const fetchAssessmentData = async () => {
      try {
        setLoading(true);
        setError(null);
        if (candidate && currentAssessment && token) {
          const response = await GetCandidateScore(
            candidate.id,
            token,
            roleId
          );

          const cs: CandidateScore[] = Array.isArray(response) ? response : response.automatedScores || [];

          const currentScore = cs.find(
            (c) => c.roleStatus.id === currentAssessment?.id
          );
          setCandidateScore(currentScore);

          if (!currentScore?.roleAssessmentMappingId) return;
          const answersData = await GetQuestionAnsweredByCandidate(
            currentScore?.roleAssessmentMappingId,
            candidate.id,
            token
          );

          const processedData = processApiData(answersData);
          
          setAssessmentData(processedData);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    if (candidate && roleId) {
      fetchAssessmentData();
    }
  }, [candidate, currentAssessment, token, roleId]);

  const processApiData = (answersData: any[]): Test => {
    const skillsMap = new Map<string, Skill>();

    skillsMap.set("No Skill", {
      name: "No Skill",
      questions: [],
      questionsTotal: 0,
      questionsAnswered: 0,
      questionsCorrect: 0,
      questionsWrong: 0,
      questionsSkipped: 0,
      score: 0,
    });

    answersData.forEach((answer) => {
      const questionSkills =
        answer.skills && answer.skills.length > 0
          ? answer.skills
          : [{ name: "No Skill" }];

      questionSkills.forEach((skill: { name: string }) => {
        if (!skillsMap.has(skill.name)) {
          skillsMap.set(skill.name, {
            name: skill.name,
            questions: [],
            questionsTotal: 0,
            questionsAnswered: 0,
            questionsCorrect: 0,
            questionsWrong: 0,
            questionsSkipped: 0,
            score: 0,
          });
        }

        const skillData = skillsMap.get(skill.name)!;

        const codeTypes = ["sql", "javascript", "react", "js"];
        const isCodeType = codeTypes.includes(answer.type?.toLowerCase() ?? "");

        // For code questions the candidateAnswers[].id is always null (MCQ Answer FK is unused).
        // Use isUnanswered from the backend which checks CandidateMediaAnswerId instead.
        const isSkipped = answer.isUnanswered ||
          (!isCodeType &&
            answer.candidateAnswers &&
            answer.candidateAnswers.length === 1 &&
            answer.candidateAnswers[0].id === null);

        // manualEvaluation: 0=Incorrect, 1=Partial, 2=Correct (mirrors ManualEvaluationStatus enum)
        let result: Question["result"];
        if (isSkipped) {
          result = "skipped";
        } else if (isCodeType && answer.manualEvaluation != null) {
          result = answer.manualEvaluation === 2 ? "correct" : "wrong";
        } else {
          result = answer.isCorrect ? "correct" : "wrong";
        }

        const candidateAnswerTexts: string[] = (answer.candidateAnswers ?? [])
          .filter((a: any) => a.text != null)
          .map((a: any) => String(a.text));

        const correctAnswerTexts: string[] = (answer.options ?? [])
          .filter((o: any) => o.isCorrect)
          .map((o: any) => String(o.text));

        skillData.questions.push({
          id: answer.questionId,
          question: answer.question.replace(/<[^>]*>/g, ""),
          result,
          skills: answer.skills,
          accuracy: answer.accuracy,
          time: answer.time,
          type: answer.type,
          candidateAnswerTexts,
          correctAnswerTexts,
          manualEvaluation: answer.manualEvaluation ?? null,
        });

        skillData.questionsTotal++;
        if (isSkipped) {
          skillData.questionsSkipped++;
        } else {
          skillData.questionsAnswered++;
          if (result === "correct") {
            skillData.questionsCorrect++;
          } else {
            skillData.questionsWrong++;
          }
        }
      });
    });

    skillsMap.forEach((skill) => {
      skill.score =
        skill.questionsTotal > 0
          ? (skill.questionsCorrect / skill.questionsTotal) * 100
          : 0;
    });

    if (skillsMap.get("No Skill")?.questionsTotal === 0) {
      skillsMap.delete("No Skill");
    }

    return {
      id: 1,
      skills: Array.from(skillsMap.values()),
    };
  };

  const StatusBadge = ({ result }: { result: Question["result"] }) => {
    const styles = {
      correct: "bg-green-100 text-green-700",
      wrong: "bg-red-100 text-red-700",
      skipped: "bg-gray-100 text-gray-700",
    };

    const icons = {
      correct: <CheckBadgeIcon className="w-4 h-4" />,
      wrong: <XCircleIcon className="w-4 h-4" />,
      skipped: <ArrowRightCircleIcon className="w-4 h-4" />,
    };

    return (
      <div
        className={`flex items-center gap-2 px-2 py-1 rounded-full ${styles[result]}`}
      >
        {icons[result]}
        <span className="text-sm capitalize">{result}</span>
      </div>
    );
  };

  const SkillScoreBox = ({ skill }: { skill: Skill }) => {
    return (
      <div className="bg-white rounded-lg shadow p-4 border border-gray-200 w-full max-w-md mx-auto">
        <div className="flex flex-col items-start mb-2">
          <div className="flex items-center gap-2">
            <TrophyIcon className="w-5 h-5 text-blue-600" />
            <h4 className="font-semibold text-base sm:text-xs md:text-sm lg:text-lg">
              {skill.name}
            </h4>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-blue-600 sm:text-xs md:text-sm lg:text-lg">
              {skill.score.toFixed(1)}%
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between mb-4">
          <div className="text-right">
            <p className=" text-gray-600 text-xs">
              Total Questions: {skill.questionsTotal}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-2">
          <div className="text-center p-2 bg-green-50 rounded-lg">
            <p className=" font-semibold text-green-600 text-xs">
              {skill.questionsCorrect}
            </p>
            <p className="text-xs text-gray-500 ">Correct</p>
          </div>
          <div className="text-center p-2 bg-red-50 rounded-lg">
            <p className=" font-semibold text-red-600 text-xs">
              {skill.questionsWrong}
            </p>
            <p className="text-xs text-gray-500 ">Wrong</p>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <p className=" font-semibold text-gray-600 text-xs">
              {skill.questionsSkipped}
            </p>
            <p className="text-xs text-gray-500">Skipped</p>
          </div>
        </div>
      </div>
    );
  };
  const QuestionsSection = ({
    questions,
    skills,
  }: {
    questions: Question[];
    skills: Skill[];
  }) => {
    const statusFilteredQuestion = questions.filter(
      (q) => selectedStatus === "all" || q.result === selectedStatus
    );
    return (
      <div className="bg-white rounded-lg shadow-md">
        <div className="mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-2 p-2">
            {skills.map((skill) => (
              <SkillScoreBox key={skill.name} skill={skill} />
            ))}
          </div>
        </div>
        <div className="p-4">
          <div className="flex justify-between items-center mb-2">
            <div className="text-lg font-semibold"></div>
            <div className="flex space-x-2">
              {currentTest && (
                <SkillFilter
                  skills={currentTest.skills}
                  selectedSkill={selectedSkill}
                  onSkillChange={setSelectedSkill}
                />
              )}
              <StatusFilter />
            </div>
          </div>
          <div className=" overflow-y-auto max-h-[400px] text-sm">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-white">
                <tr className="bg-gray-50">
                  <th className="px-3 py-1 text-left border">Question</th>
                  <th className="px-3 py-1 text-left border">Skills</th>
                  <th className="px-3 py-1 text-left border">Difficulty</th>
                  <th className="px-3 py-1 text-left border">Time</th>
                  <th className="px-3 py-1 text-center border">Status</th>
                </tr>
              </thead>
              <tbody>
                {statusFilteredQuestion.map((q) => {
                  const isExpanded = expandedQuestionIds.has(q.id);
                  const toggleExpand = () =>
                    setExpandedQuestionIds((prev) => {
                      const next = new Set(prev);
                      isExpanded ? next.delete(q.id) : next.add(q.id);
                      return next;
                    });

                  const codeTypes = ["sql", "javascript", "react", "js"];
                  const isCodeType = codeTypes.includes(q.type?.toLowerCase() ?? "");
                  const isTextType = ["text", "video", "audio"].includes(q.type?.toLowerCase() ?? "");

                  return (
                    <React.Fragment key={q.id}>
                      <tr
                        className="border-b hover:bg-gray-50 cursor-pointer select-none"
                        onClick={toggleExpand}
                      >
                        <td className="px-3 py-1 border max-w-2xl text-wrap">
                          <div className="flex items-start gap-1">
                            {isExpanded
                              ? <ChevronUpIcon className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />
                              : <ChevronDownIcon className="w-3 h-3 text-gray-400 mt-0.5 shrink-0" />}
                            <span>{q.question}</span>
                          </div>
                        </td>
                        <td className="px-3 py-1 border">
                          {q.skills.map((s) => (
                            <span key={s.name}>{s.name} </span>
                          ))}
                        </td>
                        <td className="px-3 py-1 border">{q.accuracy} %</td>
                        <td className="px-3 py-1 border">{q.time}s</td>
                        <td className="px-3 py-1 border text-center">
                          <StatusBadge result={q.result} />
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="bg-blue-50/40">
                          <td colSpan={5} className="px-5 py-3 border-b">
                            {q.result === "skipped" ? (
                              <p className="text-xs text-gray-500 italic">Question not answered.</p>
                            ) : isTextType ? (
                              <p className="text-xs text-green-600">Text / media answer recorded — counted as correct.</p>
                            ) : isCodeType ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">Code question — auto-graded:</span>
                                <span className={`text-xs font-semibold ${
                                  q.manualEvaluation === 2
                                    ? "text-green-600"
                                    : q.manualEvaluation === 0
                                    ? "text-red-600"
                                    : "text-gray-400"
                                }`}>
                                  {q.manualEvaluation === 2
                                    ? "✓ Correct"
                                    : q.manualEvaluation === 0
                                    ? "✗ Incorrect"
                                    : "Pending evaluation"}
                                </span>
                              </div>
                            ) : (
                              <div className="flex flex-col gap-2">
                                <div className="flex items-start gap-2">
                                  <span className="text-xs text-gray-500 shrink-0 pt-0.5">Your answer:</span>
                                  {q.candidateAnswerTexts && q.candidateAnswerTexts.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {q.candidateAnswerTexts.map((text, i) => (
                                        <span
                                          key={i}
                                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                            q.result === "correct"
                                              ? "bg-green-100 text-green-700"
                                              : "bg-red-100 text-red-700"
                                          }`}
                                        >
                                          {text}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400 italic">—</span>
                                  )}
                                </div>

                                {q.result !== "correct" &&
                                  q.correctAnswerTexts &&
                                  q.correctAnswerTexts.length > 0 && (
                                    <div className="flex items-start gap-2">
                                      <span className="text-xs text-gray-500 shrink-0 pt-0.5">Correct answer:</span>
                                      <div className="flex flex-wrap gap-1">
                                        {q.correctAnswerTexts.map((text, i) => (
                                          <span
                                            key={i}
                                            className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700"
                                          >
                                            {text}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const SkillFilter = ({
    skills,
    selectedSkill,
    onSkillChange,
  }: {
    skills: Skill[];
    selectedSkill: string;
    onSkillChange: (skill: string) => void;
  }) => {
    return (
      <div className="flex items-center gap-4">
        <select
          value={selectedSkill}
          onChange={(e) => onSkillChange(e.target.value)}
          className="border rounded-md p-2 w-20 text-xs"
        >
          <option value="all">All Skills</option>
          {skills.map((skill) => (
            <option key={skill.name} value={skill.name}>
              {skill.name}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const StatusFilter = () => {
    const status = ["correct", "wrong", "skipped"];
    return (
      <div className="flex items-center gap-4">
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="border rounded-md p-2 w-20 text-xs"
        >
          <option value="all">All</option>
          {status.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const ScoreSummary = ({ questions }: { questions: Question[] }) => {
    const correctQuestions = questions.filter(
      (q) => q.result === "correct"
    ).length;
    const wrongQuestions = questions.filter((q) => q.result === "wrong").length;
    const skippedQuestions = questions.filter(
      (q) => q.result === "skipped"
    ).length;

    return (
      <div className="bg-white rounded-lg shadow-md px-6 py-2">
        <h3 className="text-lg font-semibold mb-4">Overall Score</h3>
        <div className="grid grid-cols-5 gap-2">
          <div className="text-center">
            <p className="text-xs md:text-sm lg:text-md xl:text-lg font-bold text-blue-600">
              {candidateScore?.score}
            </p>
            <p className="text-[10px] md:text-xs lg:text-sm xl:text-base text-gray-500">
              Score
            </p>
          </div>

          <div className="text-center">
            <p className="text-xs md:text-sm lg:text-md xl:text-lg font-bold text-green-500">
              {correctQuestions}
            </p>
            <p className="text-[10px] md:text-xs lg:text-sm xl:text-base text-gray-500">
              Correct
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs md:text-sm lg:text-md xl:text-lg font-bold text-red-500">
              {wrongQuestions}
            </p>
            <p className="text-[10px] md:text-xs lg:text-sm xl:text-base text-gray-500">
              Wrong
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs md:text-sm lg:text-md xl:text-lg font-bold text-gray-500">
              {skippedQuestions}
            </p>
            <p className="text-[10px] md:text-xs lg:text-sm xl:text-base text-gray-500">
              Skipped
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs md:text-sm lg:text-md xl:text-lg font-bold text-gray-500">
              {candidateScore?.countOfTimesMouseOutOfTestWindow}
            </p>
            <p className="text-[10px] md:text-xs lg:text-sm xl:text-base text-gray-500">
              Moved Away
            </p>
          </div>
        </div>
      </div>
    );
  };

  let currentTest: Test | null = null;
  let filteredSkills: Skill[] = [];
  let allQuestions: Question[] = [];
  if (assessmentData) currentTest = assessmentData;
  if (currentTest) {
    filteredSkills =
      selectedSkill === "all"
        ? currentTest.skills
        : currentTest.skills.filter((skill) => skill.name === selectedSkill);
      
     allQuestions = [
      ...new Map(
        currentTest.skills.flatMap((s) => s.questions).map((q) => [q.id, q])
      ).values(),
    ];
  
  }

  const allFilteredQuestions = Array.from(
    new Map(
      filteredSkills
        .flatMap((skill) => skill.questions)
        .map((question) => [question.id, question])
    ).values()
  );

  filteredSkills
  .flatMap(skill => skill.questions)
  .forEach(q => console.log(q.id,"asd"));

  const result = (
    <div className="fixed inset-0 flex justify-center z-50 h-screen backdrop-blur-sm">
      <div
        className="relative p-2 shadow-lg md:w-[70%] w-[95%] max-h-[90%] overflow-y-auto dark:bg-darkbg top-14 rounded-md border border-gray-300 bg-gray-100"
        ref={modalRef}
      >
        <div className="mx-auto px-4 pt-0 text-gray-600 ">
          <div className="bg-white rounded-lg shadow-md mb-2 flex justify-between items-center">
            <div className="p-2">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                  <UserCircleIcon className="w-6 h-6 text-white" />
                </div>
                <div className="flex items-center space-x-2">
                  <h2 className="text-lg font-semibold">
                    {candidate &&
                      candidate?.firstName + " " + candidate?.lastName}
                  </h2>
                  <p className="text-gray-500">{candidate?.email}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex space-x-4 border-b border-gray-300">
            {assessments.map((a) => (
              <div
                key={a.id}
                onClick={(e) => {
                  // Stop the event from bubbling through React's virtual tree.
                  // Without this, the click reaches the candidate-name <a> link
                  // (rendered in RoleCandidatesList) via portal event bubbling
                  // and navigates away to the candidate profile page.
                  e.stopPropagation();
                  setCurrentAssessment(a);
                }}
                className={`cursor-pointer px-4 py-2 text-sm font-medium ${
                  currentAssessment?.id === a.id
                    ? "border-b-2 border-blue-500 text-blue-500"
                    : "text-gray-500 hover:text-blue-500"
                }`}
              >
                {a.questionairre.title}
              </div>
            ))}
          </div>
          {loading && (
            <div className="flex items-center justify-center min-h-[400px]">
              <Spinner height="h-8" width="h-8" />
            </div>
          )}
          {!loading && error && (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load assessment data: {error}
              </AlertDescription>
            </Alert>
          )}
          {!loading && !error && !assessmentData && (
            <Alert>
              <AlertDescription>No assessment data available</AlertDescription>
            </Alert>
          )}
          {!loading && !error && candidateScore && currentTest ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg shadow-md px-6 grid grid-cols-2">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <ClockIcon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Time Spent</p>
                      <p className="text-sm font-medium">
                        <div>
                          {candidateScore && candidateScore.endTime && (
                            <div className="flex flex-row items-center gap-2">
                              {(() => {
                                const startTime = new Date(
                                  candidateScore.startTime
                                );
                                const endTime = new Date(
                                  candidateScore.endTime
                                );
                                const timeDiffInMs =
                                  endTime.getTime() - startTime.getTime();
                                const minutes = Math.floor(
                                  timeDiffInMs / (1000 * 60)
                                );
                                const seconds = Math.floor(
                                  (timeDiffInMs % (1000 * 60)) / 1000
                                );

                                // The duration the exam clock actually enforces is the
                                // QUESTIONNAIRE's Time, not the parent AssessmentTemplate's
                                // (see CandidatesAnswersController.CalculateRemainingSecondsAsync).
                                // One template is reused across questionnaires with different
                                // durations, so reading assessment.time here showed the wrong
                                // limit - and since the cap below saturates, that wrong limit was
                                // what the label displayed. Fall back to the template only when
                                // the questionnaire has no duration configured.
                                const configuredMinutes =
                                  currentAssessment?.questionairre?.time ??
                                  (currentAssessment?.assessment?.time
                                    ? Number.parseInt(
                                        currentAssessment.assessment.time
                                      )
                                    : undefined);

                                if (configuredMinutes) {
                                  const assessmentDurationInMs =
                                    Number(configuredMinutes) * 60 * 1000;
                                  // Resuming after a long gap makes endTime - startTime exceed the
                                  // real duration, so cap the label (red/green stays uncapped).
                                  const displayTimeDiffInMs = Math.min(
                                    timeDiffInMs,
                                    assessmentDurationInMs
                                  );
                                  const displayMinutes = Math.floor(
                                    displayTimeDiffInMs / (1000 * 60)
                                  );
                                  const displaySeconds = Math.floor(
                                    (displayTimeDiffInMs % (1000 * 60)) / 1000
                                  );
                                  return (
                                    <div
                                      className={`${timeDiffInMs < assessmentDurationInMs / 2 ? "bg-red-400" : "bg-green-400"} w-fit text-white p-1 rounded-lg dark:text-gray-800 texxt-sm my-2 `}
                                    >
                                      {`${displayMinutes}:${displaySeconds < 10 ? "0" : ""}${displaySeconds} mins`}
                                    </div>
                                  );
                                }
                                return (
                                  <div
                                    className={`bg-gray-400 w-fit text-white p-1 rounded-lg`}
                                  >
                                    {`${minutes}:${seconds < 10 ? "0" : ""}${seconds} mins`}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <AcademicCapIcon className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Skills Tested</p>
                      <p className="text-lg font-medium">
                        {
                          currentTest.skills.filter(
                            (ct) => ct.name !== "No Skill"
                          ).length
                        }
                      </p>
                    </div>
                  </div>
                </div>
                <ScoreSummary questions={allQuestions} />
              </div>

              <QuestionsSection
                questions={allFilteredQuestions}
                skills={filteredSkills}
              />
            </>
          ) : (
            !loading &&
            !error &&
            assessmentData && (
              <div className="mt-6 bg-white rounded-lg shadow-md p-4 text-center text-xl text-entntorange">
                This assessment has not been taken yet.
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(result, document.getElementById("root") as HTMLElement);
};

export default CandidateAssessmentResult;
